import { Code, ConnectError } from "@connectrpc/connect";
import { enumToJson } from "@bufbuild/protobuf";
import { ErrorCodeSchema, ErrorMessageSchema } from "../gen/general_pb";
import { ErrorCode } from "../proto/general";

/** What a failed call tells the UI. `code` is the i18n key; `args` fills its template. */
export type ApiError = {
  code?: ErrorCode;
  args: Record<string, string>;
  /** Kept apart from `code`: folding them would hide AUTH_ codes arriving as Code.Unknown. */
  serverFault: boolean;
  /** Developer text. Logs and diagnostics only, never shown to a user. */
  message: string;
};

/** Mirrors errs.Error.IsServerFault() on the backend. */
const SERVER_FAULT_CODES = new Set([Code.Internal, Code.Unavailable, Code.DataLoss, Code.Unknown]);

/** The proto enum name: connect-es drops the prefix, templates and comparisons keep it. */
function codeName(value: number): ErrorCode | undefined {
  try {
    const name = enumToJson(ErrorCodeSchema, value);
    return typeof name === "string" ? (name as ErrorCode) : undefined;
  } catch {
    // A number this client's schema does not know. Better no code than a wrong one.
    return undefined;
  }
}

export function apiError(error: unknown): ApiError {
  const connectError = ConnectError.from(error);
  const detail = connectError.findDetails(ErrorMessageSchema)[0];

  return {
    code: detail ? codeName(detail.code) : undefined,
    args: detail?.args ?? {},
    // A raw server error has no detail, so this is all it carries.
    serverFault: detail === undefined && SERVER_FAULT_CODES.has(connectError.code),
    message: connectError.rawMessage,
  };
}
